import React, { useState, useEffect, useMemo, useRef } from 'react';
import { adminAPI } from '../../services/api';

// Admin sets a restaurant's POS print (format) settings. Pushed to that restaurant's
// POS web app, which adopts them once per save (push model — the restaurant can still
// tweak locally afterward; re-saving re-pushes). The physical printer selection stays
// per-terminal (admin can't know each terminal's OS printer). No app release.

const DEFAULTS = {
  printEnabled: 'true', paperWidth: '58', paperHeight: '150', fontSize: '9',
  fontWeight: 'bold', lineHeight: '1.15', marginLeft: '2', marginRight: '2',
  marginBottom: '0', printerType: 'escpos', printDirection: '1', printDensity: '8',
  printSpeed: '4', rasterBandHeight: '0', kotEnabled: 'true', kotOnly: 'false',
};

function PrintSettingsManagement() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selId, setSelId] = useState('');
  const [form, setForm] = useState(DEFAULTS);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  // Live preview fetched from the restaurant's POS terminal
  const [pvLoading, setPvLoading] = useState(false);
  const [pvImg, setPvImg] = useState('');
  const [pvMsg, setPvMsg] = useState('');
  const [pvKind, setPvKind] = useState('receipt');
  const pollRef = useRef(null);

  useEffect(() => { load(); }, []);

  // Stop polling on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  // Ask the selected restaurant's POS terminal to render the EXACT preview of the
  // settings currently in the form, then poll for the uploaded image.
  const requestPreview = async (kind = 'receipt') => {
    if (!selId) return;
    stopPoll();   // a new request supersedes any in-flight one (allows receipt↔KOT switch)
    setPvKind(kind);
    setPvLoading(true);
    setPvImg('');
    setPvMsg('Asking the terminal…');
    try {
      const res = await adminAPI.requestPrintPreview(selId, { ...form, kind });
      const { requestId, online } = res.data || {};
      if (!requestId) { setPvLoading(false); setPvMsg('Could not start the preview.'); return; }
      if (!online) setPvMsg('Terminal looks offline — waiting a moment in case it reconnects…');
      const started = Date.now();
      pollRef.current = setInterval(async () => {
        if (Date.now() - started > 25000) {
          stopPoll();
          setPvLoading(false);
          setPvMsg(online
            ? 'No response from the terminal. Make sure the POS app is open and updated (v1.0.16+).'
            : 'Terminal is offline. Open the POS app on that terminal and try again.');
          return;
        }
        try {
          const p = await adminAPI.getPrintPreview(requestId);
          const d = p.data || {};
          if (d.status === 'ready' && d.dataUrl) {
            stopPoll(); setPvImg(d.dataUrl); setPvLoading(false); setPvMsg('');
          } else if (d.status === 'error') {
            stopPoll(); setPvLoading(false); setPvMsg(d.error || 'The terminal could not render the preview.');
          }
        } catch (_) { /* 404 until the terminal uploads — keep polling until timeout */ }
      }, 1500);
    } catch (e) {
      setPvLoading(false);
      setPvMsg('Failed: ' + (e.response?.data?.message || e.message));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAllRestaurants({ limit: 500 });
      const list = res.data?.data?.restaurants || res.data?.restaurants || [];
      setRestaurants(Array.isArray(list) ? list : []);
      setError('');
    } catch (e) { setError('Failed to load restaurants'); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return restaurants
      .filter(r => !q || (r.name || '').toLowerCase().includes(q) || (r.address?.city || '').toLowerCase().includes(q))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [restaurants, search]);

  const selected = restaurants.find(r => r._id === selId);

  const pick = (r) => {
    setSelId(r._id);
    const ps = r.printSettings || {};
    setForm({ ...DEFAULTS, ...Object.fromEntries(Object.keys(DEFAULTS).map(k => [k, ps[k] != null && ps[k] !== '' ? String(ps[k]) : DEFAULTS[k]])) });
    setPushEnabled(ps.enabled !== false);   // default true unless explicitly disabled
    setSavedMsg('');
    stopPoll(); setPvImg(''); setPvMsg(''); setPvLoading(false);
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await adminAPI.updateRestaurantPrintSettings(selId, { ...form, enabled: pushEnabled });
      // reflect the new updatedAt/enabled in local list so re-selecting is fresh
      setRestaurants(prev => prev.map(r => r._id === selId
        ? { ...r, printSettings: { ...r.printSettings, ...form, enabled: pushEnabled, updatedAt: new Date().toISOString() } } : r));
      setSavedMsg(pushEnabled ? 'Saved & pushed to the restaurant POS ✓' : 'Saved (pushing disabled — restaurant uses its own settings)');
      setTimeout(() => setSavedMsg(''), 3500);
    } catch (e) { alert('Failed: ' + (e.response?.data?.message || e.message)); }
    finally { setSaving(false); }
  };

  const managedCount = restaurants.filter(r => r.printSettings?.enabled).length;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', margin: 0 }}>POS Print Settings</h1>
      <p style={{ color: '#64748B', fontSize: 14, margin: '6px 0 16px' }}>
        Set a restaurant's receipt/KOT format for its POS. Saved settings are pushed to that restaurant's app (adopted on its next order/print).
        The restaurant can still fine-tune on its terminal; re-save here to push again. The physical printer is chosen on the terminal.
        <b> {managedCount}</b> restaurant(s) currently admin-managed.
      </p>

      {error ? <p style={{ color: '#EF4444' }}>{error}</p> : null}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Restaurant picker */}
        <div style={{ flex: '1 1 280px', minWidth: 260 }}>
          <input style={inp} placeholder="Search restaurant / city…" value={search} onChange={e => setSearch(e.target.value)} />
          <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, marginTop: 8, maxHeight: 460, overflowY: 'auto' }}>
            {loading ? <p style={{ padding: 14, color: '#64748B' }}>Loading…</p> : filtered.map((r, i) => (
              <div key={r._id} onClick={() => pick(r)} style={{ padding: '10px 12px', cursor: 'pointer', borderTop: i ? '1px solid #F1F5F9' : 'none', background: selId === r._id ? '#EEF2FF' : '#fff' }}>
                <div style={{ fontWeight: 600, color: '#0F172A', fontSize: 14 }}>{r.name}</div>
                <div style={{ fontSize: 12, color: '#94A3B8' }}>
                  {r.address?.city || '—'}
                  {r.printSettings?.enabled ? <span style={{ marginLeft: 6, color: '#4338CA' }}>· ⚙ managed</span> : null}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Editor */}
        <div style={{ flex: '2 1 420px', minWidth: 320 }}>
          {!selected ? (
            <p style={{ color: '#94A3B8', padding: 20, border: '1px dashed #E2E8F0', borderRadius: 10, textAlign: 'center' }}>Select a restaurant to edit its print settings.</p>
          ) : (
            <div style={{ border: '1px solid #E2E8F0', borderRadius: 12, padding: 18 }}>
              <div style={{ fontWeight: 700, color: '#0F172A', marginBottom: 12 }}>{selected.name} <span style={{ color: '#94A3B8', fontWeight: 400, fontSize: 13 }}>· {selected.address?.city || ''}</span></div>

              <label style={rowChk}><input type="checkbox" checked={pushEnabled} onChange={e => setPushEnabled(e.target.checked)} /> <b>Manage this restaurant's print settings from admin</b> (uncheck to hand control back to the restaurant)</label>

              <div style={{ opacity: pushEnabled ? 1 : 0.5, pointerEvents: pushEnabled ? 'auto' : 'none' }}>
                <div style={grid}>
                  <Field label="Paper width"><select style={inp} value={form.paperWidth} onChange={e => set('paperWidth', e.target.value)}><option value="58">58 mm</option><option value="80">80 mm</option></select></Field>
                  <Field label="Printer type"><select style={inp} value={form.printerType} onChange={e => set('printerType', e.target.value)}><option value="escpos">ESC/POS (Most Printers)</option><option value="tspl">TSPL (TSC Printers)</option></select></Field>
                  <Field label="Font size"><select style={inp} value={form.fontSize} onChange={e => set('fontSize', e.target.value)}>
                    {['6','7','8','9','10','11','12','13','14','16','18','20','22','24','28','32'].map(v => <option key={v} value={v}>{v}px{v==='9'?' (Normal)':v==='6'?' (Micro)':v==='32'?' (Max)':''}</option>)}
                  </select></Field>
                  <Field label="Font weight"><select style={inp} value={form.fontWeight} onChange={e => set('fontWeight', e.target.value)}><option value="bold">Bold</option><option value="normal">Normal</option></select></Field>
                  <Field label="Line spacing"><select style={inp} value={form.lineHeight} onChange={e => set('lineHeight', e.target.value)}><option value="0.9">0.9 (Extra Tight)</option><option value="1.0">1.0 (Tight)</option><option value="1.15">1.15 (Compact)</option><option value="1.3">1.3 (Normal)</option><option value="1.5">1.5 (Relaxed)</option><option value="1.8">1.8 (Spacious)</option></select></Field>
                  <Field label="Paper height (mm)"><input style={inp} type="number" value={form.paperHeight} onChange={e => set('paperHeight', e.target.value)} /></Field>
                  <Field label="Left margin"><select style={inp} value={form.marginLeft} onChange={e => set('marginLeft', e.target.value)}>{['0','1','2','3','4','6','8'].map(v => <option key={v} value={v}>{v}px{v==='0'?' (None)':v==='2'?' (Default)':''}</option>)}</select></Field>
                  <Field label="Right margin"><select style={inp} value={form.marginRight} onChange={e => set('marginRight', e.target.value)}>{['0','1','2','3','4','6','8'].map(v => <option key={v} value={v}>{v}px{v==='0'?' (None)':v==='2'?' (Default)':''}</option>)}</select></Field>
                  <Field label="Bottom margin (mm)"><input style={inp} type="number" value={form.marginBottom} onChange={e => set('marginBottom', e.target.value)} /></Field>
                  <Field label="Print darkness"><select style={inp} value={form.printDensity} onChange={e => set('printDensity', e.target.value)}><option value="4">Light (4)</option><option value="6">Medium-Light (6)</option><option value="8">Normal (8)</option><option value="10">Medium-Dark (10)</option><option value="12">Dark (12)</option><option value="15">Max (15)</option></select></Field>
                  <Field label="Print speed"><select style={inp} value={form.printSpeed} onChange={e => set('printSpeed', e.target.value)}><option value="1">1 (Slowest / Best Quality)</option><option value="2">2</option><option value="3">3</option><option value="4">4 (Default)</option><option value="5">5</option><option value="6">6 (Fastest)</option></select></Field>
                  <Field label="Band raster (fix partial print)"><select style={inp} value={form.rasterBandHeight} onChange={e => set('rasterBandHeight', e.target.value)}><option value="0">Off (default)</option><option value="128">On — 128 rows</option><option value="256">On — 256 rows (recommended)</option><option value="512">On — 512 rows</option></select></Field>
                  <Field label="Print direction"><select style={inp} value={form.printDirection} onChange={e => set('printDirection', e.target.value)}><option value="0">Normal (0°)</option><option value="1">Flipped (180°)</option></select></Field>
                </div>
                <label style={rowChk}><input type="checkbox" checked={form.printEnabled === 'true'} onChange={e => set('printEnabled', e.target.checked ? 'true' : 'false')} /> Printing enabled (master toggle)</label>
                <label style={rowChk}><input type="checkbox" checked={form.kotEnabled === 'true'} onChange={e => set('kotEnabled', e.target.checked ? 'true' : 'false')} /> Print KOT (kitchen ticket) with each receipt</label>
                <label style={rowChk}><input type="checkbox" checked={form.kotOnly === 'true'} onChange={e => set('kotOnly', e.target.checked ? 'true' : 'false')} /> Print ONLY KOT — skip the customer receipt (overrides the option above)</label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                <button onClick={save} disabled={saving} style={btn('#2563EB')}>{saving ? 'Saving…' : 'Save & push'}</button>
                {savedMsg ? <span style={{ color: '#166534', fontSize: 13, fontWeight: 600 }}>{savedMsg}</span> : null}
              </div>

              {/* Live preview fetched from the restaurant's POS terminal */}
              <div style={{ marginTop: 20, borderTop: '1px solid #EEF2F7', paddingTop: 16 }}>
                <div style={{ fontWeight: 700, color: '#0F172A', marginBottom: 4 }}>Live preview from the terminal</div>
                <p style={{ fontSize: 12.5, color: '#64748B', margin: '0 0 12px' }}>
                  Renders the receipt on the restaurant's POS terminal with the settings above and shows it here — the exact
                  black/white image that printer outputs. Needs the POS app open on that terminal (v1.0.16+).
                </p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <button onClick={() => requestPreview('receipt')} disabled={pvLoading && pvKind === 'receipt'} style={btn('#111827')}>
                    {pvLoading && pvKind === 'receipt' ? 'Requesting…' : '👁 Preview receipt'}
                  </button>
                  <button onClick={() => requestPreview('kot')} disabled={pvLoading && pvKind === 'kot'} style={btn('#374151')}>
                    {pvLoading && pvKind === 'kot' ? 'Requesting…' : '👁 Preview KOT'}
                  </button>
                </div>
                {(pvImg || pvMsg || pvLoading) && (
                  <div style={{ background: '#E5E7EB', borderRadius: 10, padding: 16, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
                    {pvLoading && !pvImg
                      ? <span style={{ color: '#6B7280', fontSize: 13 }}>{pvMsg || 'Rendering…'}</span>
                      : pvImg
                        ? <img src={pvImg} alt="Terminal print preview" style={{ width: form.paperWidth === '80' ? 340 : 250, imageRendering: 'pixelated', background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.3)' }} />
                        : <span style={{ color: '#B45309', fontSize: 13, textAlign: 'center' }}>{pvMsg}</span>}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4 }}>{label}</label>{children}</div>
);
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 12, marginBottom: 8 };
const rowChk = { display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: '#334155', margin: '8px 0' };
const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' });
const inp = { width: '100%', padding: '9px 11px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

export default PrintSettingsManagement;
